import { toast } from 'react-toastify';

import { useLoading } from '@/contextProviders/loadingProvider';
import { useFetchWithAuth } from '@/queries/useFetchWithAuth';
import { formatDate } from '@/utils/dateUtils';
import { toastLoadingOptions, toastSuccessOptions } from '@/utils/toastOptions';

export interface Executable {
  url: string;
  onSuccess: string;
  onError: string;
}

export function useVoorraad() {
  const { setLoading } = useLoading();
  const { fetchWithAuth } = useFetchWithAuth();

  const downloadBestand = async (responseData: string) => {
    const blob = new Blob([responseData], { type: 'text/plain' });
    const downloadUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = downloadUrl;

    const datum = new Date();
    datum.setDate(datum.getDate() + 1);
    link.download = `voorraad-${formatDate(datum)}.txt`;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  };

  const execute = async ({ url, onSuccess, onError }: Executable) => {
    const loadingToast = toast.loading('Bezig met TAM-voorraad ophalen...', toastLoadingOptions);
    try {
      setLoading(true);

      const response = await fetchWithAuth(url, {
        method: 'GET',
      });

      const responseData = (await response.text()) as string;

      switch (true) {
        case response.status <= 299:
          await downloadBestand(responseData);
          console.log(`Response met voorraad ontvangen`);
          toast.success(onSuccess, toastSuccessOptions);
          break;
        case response.status >= 500:
          toast.error(
            'Voorraad ophalen is niet gelukt vanwege een technische fout. Probeer het nog een keer of neem contact op met support.',
            { position: 'top-center' },
          );
          break;
        case response.status >= 400:
          toast.error(onError, { position: 'top-center' });
          break;
        default:
          toast.error('Er is een onbekende fout opgetreden. Neem contact op met support.', {
            position: 'top-center',
          });
          break;
      }
    } catch (error) {
      console.log(error);
    } finally {
      toast.done(loadingToast);
      setLoading(false);
    }
  };

  const tamVoorraad = async () => {
    const onSuccess = 'Voorraad ophalen is gelukt.';
    const onError = 'Voorraad ophalen is mislukt.';
    return execute({
      url: '/tam/v1/voorraad',
      onSuccess,
      onError,
    });
  };

  return {
    tamVoorraad,
  };
}
